import type { Dealer, DealerRole } from '@/types'

const EM_DASH = '—'

export function formatDate(value?: string | null): string {
  if (!value) return EM_DASH
  const d = new Date(value)
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1980) return EM_DASH
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatDateTime(value?: string | null): string {
  if (!value) return EM_DASH
  const d = new Date(value)
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1980) return EM_DASH
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function formatRelative(value?: string | null): string {
  if (!value) return 'never'
  const d = new Date(value)
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1980) return 'never'
  const seconds = Math.round((Date.now() - d.getTime()) / 1000)
  const table: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [2592000, 'day'],
    [31536000, 'month']
  ]
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (seconds < 60) return 'just now'
  for (let i = 1; i < table.length; i++) {
    const [limit, unit] = table[i]
    if (seconds < limit) return rtf.format(-Math.floor(seconds / table[i - 1][0]), unit)
  }
  return rtf.format(-Math.floor(seconds / 31536000), 'year')
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2
  }).format(value)
}

/** Sub-dealers have a non-zero child id; "{parent}-0" is the paying account. */
export function dealerRole(d: Pick<Dealer, 'is_admin' | 'child_user_id'>): DealerRole {
  if (d.is_admin) return 'admin'
  return d.child_user_id === 0 ? 'primary' : 'sub'
}

/** The primary dealer id that owns a given account's subscription. */
export function primaryIdOf(userId: string): string {
  return `${userId.split('-')[0]}-0`
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase()
}

/** Groups the free-form status string into the handful of tones the UI paints. */
export type StatusTone = 'pending' | 'trial' | 'paid' | 'expired' | 'admin'

export function statusTone(status: string): StatusTone {
  switch (status) {
    case 'Pending':
      return 'pending'
    case 'Trial':
      return 'trial'
    case 'Expired':
      return 'expired'
    case 'Admin':
      return 'admin'
    default:
      return 'paid'
  }
}
